// ============================================================================
// audio-file-transcription.ts — one transcription for an imported recording
// ----------------------------------------------------------------------------
// A Stage 1 drop and a folder import (Import Projects, the Quote Run) both turn
// an audio file (voice memo, dictation, site recording) into Inspector Notes
// through here, so the two routes cannot drift (Peter, 2026-10-23: the routes
// must behave the same).
//
// Stage 1 used to send the compressed recording whole. OpenRouter's Voxtral
// pool refuses any request carrying more than about 1 MB of audio (memory
// gotcha_voxtral_shared_pool_audio_size_limit), so anything longer than about
// a minute failed. The recording is now decoded to 16 kHz mono PCM and sent in
// pieces, the way a video's soundtrack already is.
// ============================================================================

import { notesImportHeader } from '../utils/ai-document-label';
import { plainTextToNotesHtml } from '../utils/notes-markdown';
import { AUDIO_FILE_MAX_BYTES } from './folder-audio-receipt';
import { pickModelForTask } from './model-selector';
import {
    decodeAudioToPcmWav,
    TRANSCRIPTION_PIECE_MAX_PCM_BYTES,
    transcribeAudioInPieces,
    type Stage1VideoEvidenceTranscript,
} from './stage1-video-evidence';

export { AUDIO_FILE_MAX_BYTES };
const TASK = 'audio-transcription';
// The browser decodes a long voice memo in seconds; this only ends a decoder
// that never answers (a corrupt file). Not an AI deadline.
const DECODE_TIMEOUT_MS = 120_000;
const WHISPER_EXTENSIONS = /\.(mp3|mp4|mpeg|mpga|m4a|wav|webm|flac|ogg|oga)$/i;

export interface AudioFileTranscriptionOptions {
    apiKey?: string;
    model?: string;
    audioModel?: string;
    projectId?: string | null;
    signal?: AbortSignal;
    shouldStop?: () => boolean;
    onProgress?: (progress: number, message: string) => void;
    /** Tests replace the browser decoder. */
    dependencies?: { decode?: typeof decodeAudioToPcmWav };
}

function abortError(): Error {
    return Object.assign(new Error('Transcription cancelled'), { name: 'AbortError' });
}

function sizeMb(bytes: number): string {
    return (bytes / 1024 / 1024).toFixed(1);
}

async function transcribeWithWhisper(file: File, apiKey: string, signal?: AbortSignal): Promise<Stage1VideoEvidenceTranscript> {
    // An explicit OpenAI key (dev and admin only) takes the original file, as
    // Stage 1 always sent it: Whisper accepts compressed audio up to 25 MB.
    const payload = new FormData();
    const name = String(file.name || 'audio');
    payload.append('file', file, WHISPER_EXTENSIONS.test(name) ? name : name.replace(/\.[^.]+$/, '.mp3'));
    payload.append('model', 'whisper-1');
    payload.append('response_format', 'verbose_json');
    payload.append('timestamp_granularities[]', 'segment');
    const response = await fetch('https://api.openai.com/v1/audio/transcriptions', {
        method: 'POST',
        headers: { Authorization: `Bearer ${apiKey}` },
        body: payload,
        ...(signal ? { signal } : {}),
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error?.error?.message || `Transcription failed: ${response.status}`);
    }
    const result = await response.json();
    return {
        text: String(result?.text || '').trim(),
        segments: Array.isArray(result?.segments) ? result.segments : [],
    };
}

/**
 * Transcribe one audio file. Throws with a plain reason when the recording
 * cannot be used (empty, over the limit, a format this browser cannot decode).
 */
export async function transcribeAudioFile(file: File, options: AudioFileTranscriptionOptions = {}): Promise<Stage1VideoEvidenceTranscript> {
    const { apiKey, signal } = options;
    if (signal?.aborted || options.shouldStop?.()) throw abortError();
    if (!apiKey) throw new Error('API key required for transcription');
    if (!file || file.size === 0) throw new Error('The recording is empty.');
    if (file.size > AUDIO_FILE_MAX_BYTES) {
        throw new Error(`The recording is over 20 MB (${sizeMb(file.size)} MB), the most QuoteMaker transcribes.`);
    }
    if (!apiKey.startsWith('sk-or-') && apiKey !== 'gateway') return transcribeWithWhisper(file, apiKey, signal);

    const model = pickModelForTask(TASK, options.audioModel || options.model);
    if (!model) throw new Error('AI model not configured. Please set a model in Settings.');
    const decode = options.dependencies?.decode || decodeAudioToPcmWav;
    let audio: Blob;
    try {
        audio = await decode(file, { ...(signal ? { signal } : {}), maxDurationSeconds: null, decodeTimeoutMs: DECODE_TIMEOUT_MS });
    } catch (cause: any) {
        if (cause?.name === 'AbortError' || signal?.aborted) throw cause;
        // A short recording in a format this browser cannot decode (WMA) still
        // fits in one request, so it is sent as it is, as Stage 1 always did.
        if (file.size > TRANSCRIPTION_PIECE_MAX_PCM_BYTES) {
            const detail = String(cause?.message || cause || 'unknown decoder error').slice(0, 120);
            throw new Error(`This browser could not decode the recording (${detail}). Convert it to MP3 or M4A and import it again.`, { cause });
        }
        audio = file;
    }
    return transcribeAudioInPieces(audio, {
        apiKey,
        model,
        task: TASK,
        projectId: options.projectId ?? null,
        ...(signal ? { signal } : {}),
        ...(options.shouldStop ? { shouldStop: options.shouldStop } : {}),
        ...(options.onProgress ? { onProgress: options.onProgress } : {}),
    });
}

/**
 * Inspector Notes after a recording's transcript is added, exactly as a
 * Stage 1 drop writes it: the transcript alone into empty notes, otherwise
 * under the audio header (which names the kind of file, never its name).
 */
export function appendAudioTranscriptToNotes(notes: unknown, source: unknown, text: string): string {
    const current = typeof notes === 'string' ? notes : '';
    return current
        ? current + plainTextToNotesHtml(`\n\n${notesImportHeader(source, 'audio')}\n${text}`)
        : plainTextToNotesHtml(text);
}
